export const INVENTORY_UPLOAD_LIMITS = {
  images: 30,
  videos: 15,
  pdfs: 15,
};

export const INVENTORY_IMAGE_CATEGORIES = [
  { id: "front", label: "Front Photo" },
  { id: "side", label: "Side Photo" },
  { id: "back", label: "Back Photo" },
  { id: "tire", label: "Tyre Photo" },
  { id: "chassis", label: "Chassis" },
  { id: "engine", label: "Engine" },
  { id: "meter", label: "Meter" },
  { id: "glass", label: "Glass" },
  { id: "crack", label: "Crack Item" },
  { id: "key", label: "Key" },
  { id: "rc", label: "RC" },
  { id: "insurance", label: "Insurance" },
  { id: "bilty", label: "Bilty" },
  { id: "jack_rod", label: "Jack Rod" },
  { id: "stepney", label: "Stepney" },
  { id: "driver", label: "Driver Photo" },
];

export const INVENTORY_VIDEO_CATEGORIES = [
  { id: "vehicle_condition", label: "Vehicle Condition" },
  { id: "videos", label: "Videos" },
];

export const INVENTORY_PDF_CATEGORIES = [
  { id: "pre_post", label: "Pre/Post Inventory" },
  { id: "inventory_sheet", label: "Inventory Sheet" },
  { id: "crane_bill", label: "Crane Bill" },
  { id: "other_bills", label: "Other Bills" },
];

export const INVENTORY_CATEGORY_LABELS = Object.fromEntries(
  [
    ...INVENTORY_IMAGE_CATEGORIES,
    ...INVENTORY_VIDEO_CATEGORIES,
    ...INVENTORY_PDF_CATEGORIES,
    { id: "other", label: "Other" },
    { id: "uncategorized", label: "Uncategorized" },
  ].map((item) => [item.id, item.label])
);

const KNOWN_CATEGORY_IDS = new Set(Object.keys(INVENTORY_CATEGORY_LABELS));

let slotSeq = 0;

export function createInventorySlotId() {
  slotSeq += 1;
  return `slot-${Date.now().toString(36)}-${slotSeq}`;
}

export function createInitialCategorySlots(categories) {
  return Object.fromEntries(
    (categories || []).map((category) => [category.id, [createInventorySlotId()]])
  );
}

export function inventoryFileKey(file) {
  return `${file.name}-${file.size}-${file.lastModified}-${file.inventoryCategory || ""}-${file.inventorySlotId || ""}`;
}

export function tagInventoryCategory(files, categoryId, slotId) {
  return (files || []).map((file) => {
    file.inventoryCategory = categoryId;
    if (slotId) file.inventorySlotId = slotId;
    return file;
  });
}

export function filesForCategory(files, categoryId) {
  return (files || []).filter((file) => file.inventoryCategory === categoryId);
}

export function filesForSlot(files, categoryId, slotId) {
  return (files || []).filter(
    (file) => file.inventoryCategory === categoryId && file.inventorySlotId === slotId
  );
}

export function mergeInventoryFiles(existing, incoming) {
  const map = new Map((existing || []).map((file) => [inventoryFileKey(file), file]));
  (incoming || []).forEach((file) => map.set(inventoryFileKey(file), file));
  return Array.from(map.values());
}

export function formatFileSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function capInventoryFiles(files, maxCount) {
  if (files.length <= maxCount) return { files, trimmed: 0 };
  return { files: files.slice(0, maxCount), trimmed: files.length - maxCount };
}

/** How many more files of this type can still be uploaded (server total cap). */
export function remainingInventorySlots(existingCount, maxCount) {
  return Math.max(0, maxCount - (existingCount || 0));
}

export function categoryFromStoredPath(storedPath, meta = []) {
  const hit = (meta || []).find((row) => row && row.path === storedPath);
  if (hit?.category && KNOWN_CATEGORY_IDS.has(hit.category)) return hit.category;
  const name = String(storedPath || "").split("/").pop() || "";
  const match = name.match(/^([a-z0-9_]+)-\d{10,}-/);
  if (match && KNOWN_CATEGORY_IDS.has(match[1])) return match[1];
  return "uncategorized";
}

export function groupStoredFiles(paths, meta, kind) {
  const groups = new Map();
  (paths || []).forEach((storedPath) => {
    const category = categoryFromStoredPath(storedPath, meta);
    if (!groups.has(category)) groups.set(category, []);
    groups.get(category).push(storedPath);
  });
  const order = [
    ...(kind === "image"
      ? INVENTORY_IMAGE_CATEGORIES
      : kind === "video"
        ? INVENTORY_VIDEO_CATEGORIES
        : INVENTORY_PDF_CATEGORIES),
    { id: "other", label: "Other" },
    { id: "uncategorized", label: "Uncategorized" },
  ];
  return order
    .map((item) => ({
      id: item.id,
      label: INVENTORY_CATEGORY_LABELS[item.id] || item.label,
      files: groups.get(item.id) || [],
    }))
    .filter((group) => group.files.length > 0);
}
